/**
 * Service command-centre classification.
 *
 * Working now
 * - Repair: an open work session (stoppedAt is null). Stage wording follows roLiveWorkPresentation.
 * - SVR: a Cleo field session in active_logging or awaiting_done_confirmation, or a scheduled
 *   visit with arrivedAt set and departedAt empty. Appointment time alone is never "on site".
 *
 * Visits today / upcoming
 * - Singapore calendar day of visitDate, from today through the next 7 days, excluding cancelled.
 * - Today, tomorrow, then later in that horizon.
 *
 * Overdue
 * - Scheduled today, not begun (no arrival, no field session), and a parseable arrival-window
 *   end is already past. Unparseable windows are not marked overdue.
 *
 * Latest SVR reports
 * - Newest of update submitted, report sent, or visit closed. Excerpt is the customer report,
 *   otherwise customer-facing findings. Internal notes are never accepted here.
 *
 * Needs attention
 * - Existing open statuses only, and never a repair that is already in Working now.
 * - Unassigned received/diagnosis, idle diagnosis, waiting for parts, idle approved repair,
 *   customer approval, declined, finance/commercial blockers, proposal ready to send,
 *   awaiting payment, then SLA when slaExtendedUntil or slaDeadline is already past.
 *
 * Recently progressed
 * - Newest meaningful audit: status change, repair completed, work started, customer approval.
 *
 * Recently fixed
 * - completedAt from authoritative repair completion, status at or after Repair complete.
 *
 * Completed recently
 * - Those completions, plus SVRs submitted or closed, inside the last 7 Singapore days.
 *
 * Audience
 * - Admin and Finance see the operational picture.
 * - Everyone else sees work assigned to them. Needs attention also includes unassigned
 *   repair orders so a technician can claim them. Recently progressed does not: an
 *   unassigned repair appears only when this technician recorded the progress.
 *   Costs, selling prices, and internal notes are not part of this model.
 */

import { hasRoleCapability, type UserRoleFields } from "./userRoles";
import {
  addSingaporeCalendarDays,
  formatSingaporeDate,
  getSingaporeDateKeyFromInstant,
  getSingaporeMinutesOfDay,
  singaporeCalendarDayBounds,
  singaporeTodayDateKey,
} from "./timezone";
import { categorizeServiceActivity, type ServiceActivityCategory } from "./serviceActivityCategory";
import { roLiveWorkPresentation } from "./roRunningPresentation";
import { JOB_STATUS_LABELS } from "./serviceConstants";
import { elapsedSeconds } from "./roWorkTimer";

export const SERVICE_OPS_LIMITS = {
  activeRepairs: 20,
  activeVisits: 20,
  upcoming: 40,
  reports: 20,
  reportsPreview: 5,
  attention: 12,
  attentionScan: 40,
  progress: 8,
  progressScan: 30,
  fixed: 5,
  recentDays: 7,
  upcomingDays: 7,
} as const;

export const CLEO_ON_SITE_STATES = ["active_logging", "awaiting_done_confirmation"] as const;

const LEGACY_ARRIVAL_WINDOWS: Record<string, [number, number]> = {
  "9am-10am": [9 * 60, 10 * 60],
  "10am-11am": [10 * 60, 11 * 60],
  "11am-12pm": [11 * 60, 12 * 60],
  "2pm-3pm": [14 * 60, 15 * 60],
  "3pm-4pm": [15 * 60, 16 * 60],
  "4pm-5pm": [16 * 60, 17 * 60],
};

/** Same window shapes the scheduler already accepts. Unrecognised text stays null. */
export function parseArrivalWindowMinutes(windowRaw: string | null | undefined): [number, number] | null {
  if (!windowRaw) return null;
  const raw = windowRaw.trim().toLowerCase();
  if (LEGACY_ARRIVAL_WINDOWS[raw]) return LEGACY_ARRIVAL_WINDOWS[raw];
  const match = raw.match(/^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const start = Number(match[1]) * 60 + Number(match[2]);
  const end = Number(match[3]) * 60 + Number(match[4]);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return [start, end];
}

export type ServiceOperationsAudience = "all" | "assigned";

export function serviceOperationsAudience(viewer: UserRoleFields | null | undefined): ServiceOperationsAudience {
  if (hasRoleCapability(viewer, ["admin", "finance"])) return "all";
  return "assigned";
}

export function isCleoOnSiteState(state: string | null | undefined): boolean {
  return CLEO_ON_SITE_STATES.includes(String(state ?? "") as (typeof CLEO_ON_SITE_STATES)[number]);
}

export function isAuthoritativeSvrOnSite(input: {
  status: string;
  arrivedAt?: string | Date | null;
  departedAt?: string | Date | null;
  cleoOnSite: boolean;
}): boolean {
  const status = input.status.trim().toLowerCase();
  if (status === "cancelled" || status === "closed" || status === "waived") return false;
  if (input.cleoOnSite) return true;
  const arrived = input.arrivedAt != null && String(input.arrivedAt).trim() !== "";
  const departed = input.departedAt != null && String(input.departedAt).trim() !== "";
  return arrived && !departed && status === "scheduled";
}

export function visitOverdue(input: {
  status: string;
  visitDate: string | Date;
  arrivalWindow: string | null;
  arrivedAt?: string | Date | null;
  onSite: boolean;
  now: Date;
}): boolean {
  if (input.onSite) return false;
  if (input.status !== "scheduled") return false;
  if (input.arrivedAt != null && String(input.arrivedAt).trim() !== "") return false;
  const visitKey = getSingaporeDateKeyFromInstant(input.visitDate);
  if (!visitKey || visitKey !== singaporeTodayDateKey(input.now)) return false;
  const windowMinutes = parseArrivalWindowMinutes(input.arrivalWindow);
  if (!windowMinutes) return false;
  const nowMinutes = getSingaporeMinutesOfDay(input.now);
  if (nowMinutes == null) return false;
  return nowMinutes > windowMinutes[1];
}

export type VisitDayBucket = "today" | "tomorrow" | "later";

export function upcomingVisitBucket(visitDate: string | Date, now: Date): VisitDayBucket | null {
  const key = getSingaporeDateKeyFromInstant(visitDate);
  if (!key) return null;
  const today = singaporeTodayDateKey(now);
  const last = addSingaporeCalendarDays(today, SERVICE_OPS_LIMITS.upcomingDays);
  if (key < today || key > last) return null;
  if (key === today) return "today";
  if (key === addSingaporeCalendarDays(today, 1)) return "tomorrow";
  return "later";
}

export function operationalNotification(status: string | null | undefined): "failed" | "no_phone" | null {
  if (status === "failed" || status === "no_phone") return status;
  return null;
}

export function svrReportExcerpt(input: {
  customerReport?: string | null;
  findingsAndActions?: string | null;
}, max = 160): string | null {
  const source = input.customerReport?.trim() || input.findingsAndActions?.trim() || "";
  if (!source) return null;
  const flat = source.replace(/\s+/g, " ");
  if (flat.length <= max) return flat;
  return `${flat.slice(0, max - 1)}…`;
}

export function svrPicReviewLabel(status: string): string {
  switch (status) {
    case "pending_review":
      return "Awaiting PIC review";
    case "report_sent":
      return "Report sent";
    case "closed":
      return "Visit closed";
    case "waived":
      return "Waived";
    case "awaiting_payment":
      return "Awaiting payment";
    case "scheduled":
      return "Scheduled";
    case "cancelled":
      return "Cancelled";
    default:
      return status.replace(/_/g, " ");
  }
}

export function svrReportOccurredAt(input: {
  updateSubmittedAt?: string | Date | null;
  reportSentAt?: string | Date | null;
  closedAt?: string | Date | null;
}): string | null {
  const times = [input.updateSubmittedAt, input.reportSentAt, input.closedAt]
    .map((value) => (value == null || String(value).trim() === "" ? null : new Date(value).getTime()))
    .filter((value): value is number => value != null && Number.isFinite(value));
  if (times.length === 0) return null;
  return new Date(Math.max(...times)).toISOString();
}

const ATTENTION_RULES: Array<{
  code: string;
  label: string;
  when: (status: string, assigned: boolean) => boolean;
}> = [
  { code: "unassigned", label: "Awaiting technician assignment", when: (status, assigned) => !assigned && (status === "received" || status === "diagnosis") },
  { code: "parts", label: "Waiting for parts", when: (status) => status === "pending_parts" },
  { code: "approval", label: "Customer approval pending", when: (status) => status === "awaiting_customer_confirmation" },
  { code: "declined", label: "Customer declined", when: (status) => status === "customer_declined" },
  { code: "finance", label: "Finance blocker", when: (status) => status === "pending_finance" || status === "awaiting_commercial_review" },
  { code: "send_proposal", label: "Proposal ready to send", when: (status) => status === "ready_to_send_to_customer" },
  { code: "diagnosis", label: "Diagnosis waiting on action", when: (status, assigned) => assigned && (status === "diagnosis" || status === "received") },
  { code: "repair_ready", label: "Repair ready to start", when: (status) => status === "in_progress" },
  { code: "payment", label: "Awaiting payment", when: (status) => status === "awaiting_payment" },
];

export function classifyRoNeedsAttention(input: {
  status: string;
  assignedTechnicianId: number | null;
  timerRunning: boolean;
  slaDeadline?: string | Date | null;
  slaExtendedUntil?: string | Date | null;
  now: Date;
}): { code: string; label: string } | null {
  if (input.timerRunning) return null;
  const status = input.status;
  if (status === "closed" || status === "repair_complete" || status === "awaiting_collection") return null;
  const assigned = input.assignedTechnicianId != null;
  const deadline = input.slaExtendedUntil ?? input.slaDeadline;
  const slaOverdue = deadline != null && String(deadline).trim() !== "" && new Date(deadline).getTime() < input.now.getTime();
  for (const rule of ATTENTION_RULES) {
    if (!rule.when(status, assigned)) continue;
    return {
      code: rule.code,
      label: slaOverdue ? `${rule.label} · SLA overdue` : rule.label,
    };
  }
  if (slaOverdue) return { code: "sla", label: "SLA overdue" };
  return null;
}

export function isMeaningfulRoProgress(action: string): boolean {
  const category = categorizeServiceActivity(action);
  if (category === "status" || category === "customer_approval") return true;
  return action === "work_session_started";
}

export function roProgressSummary(action: string, newValue?: string | null): string {
  if (action === "work_session_started") return "Work started";
  if (action === "repair_completed") return "Repair completed";
  if (action === "status_changed" || action.toLowerCase().startsWith("status changed")) {
    if (newValue) {
      try {
        const parsed = JSON.parse(newValue) as { to?: unknown };
        if (typeof parsed.to === "string" && parsed.to.trim()) {
          return `Status changed to ${parsed.to.replace(/_/g, " ")}`;
        }
      } catch {
        /* plain-language status actions carry the label in the action itself */
      }
    }
    return action.startsWith("Status changed") ? action : "Status changed";
  }
  if (/customer approved/i.test(action)) return "Customer approved";
  if (/customer declined/i.test(action)) return "Customer declined";
  if (/item collected/i.test(action)) return "Collected and closed";
  return action.replace(/_/g, " ");
}

export function recentWhenLabel(at: string | Date, now: Date): string {
  const key = getSingaporeDateKeyFromInstant(at);
  if (!key) return "";
  const today = singaporeTodayDateKey(now);
  if (key === today) return "today";
  if (key === addSingaporeCalendarDays(today, -1)) return "yesterday";
  return formatSingaporeDate(at, { day: "numeric", month: "short" });
}

export function isWithinRecentServiceWindow(at: string | Date | null | undefined, now: Date): boolean {
  if (at == null || String(at).trim() === "") return false;
  const time = new Date(at).getTime();
  if (!Number.isFinite(time)) return false;
  const startKey = addSingaporeCalendarDays(singaporeTodayDateKey(now), -(SERVICE_OPS_LIMITS.recentDays - 1));
  const start = singaporeCalendarDayBounds(startKey).startInclusive.getTime();
  return time >= start && time <= now.getTime();
}

function assignedVisible(
  audience: ServiceOperationsAudience,
  viewerId: number,
  assignedId: number | null | undefined,
  includeUnassigned: boolean,
): boolean {
  if (audience === "all") return true;
  if (assignedId == null) return includeUnassigned;
  return assignedId === viewerId;
}

/** Assigned repairs, or unassigned repairs whose progress this technician recorded. */
function progressVisible(
  audience: ServiceOperationsAudience,
  viewerId: number,
  row: { assignedTechnicianId: number | null; workerUserId: number | null },
): boolean {
  if (audience === "all") return true;
  if (row.assignedTechnicianId === viewerId) return true;
  return row.assignedTechnicianId == null && row.workerUserId === viewerId;
}

export type OpsActiveRepair = {
  repairOrderId: number;
  jobNumber: string;
  customerName: string;
  equipment: string;
  status: string;
  technicianName: string;
  technicianUserId: number;
  assignedTechnicianId: number | null;
  startedAt: string;
};

export type OpsActiveVisit = {
  id: number;
  visitNumber: string;
  customerName: string;
  siteAddress: string | null;
  technicianId: number | null;
  technicianName: string | null;
  status: string;
  startedAt: string | null;
  nextAction: string;
};

export type OpsUpcomingVisit = {
  id: number;
  visitNumber: string;
  visitDate: string;
  arrivalWindow: string | null;
  visitType: string;
  status: string;
  customerName: string;
  siteAddress: string | null;
  technicianId: number | null;
  technicianName: string | null;
  picName: string | null;
  notification: "failed" | "no_phone" | null;
  arrivedAt: string | null;
  onSite: boolean;
};

export type OpsReport = {
  id: number;
  visitNumber: string;
  customerName: string;
  siteAddress: string | null;
  technicianId: number | null;
  technicianName: string | null;
  occurredAt: string;
  excerpt: string | null;
  status: string;
  picReview: string;
};

export type OpsAttentionJob = {
  id: number;
  jobNumber: string;
  customerName: string;
  equipment: string;
  status: string;
  assignedTechnicianId: number | null;
  assignedTechnicianName: string | null;
  timerRunning: boolean;
  slaDeadline: string | null;
  slaExtendedUntil: string | null;
};

export type OpsProgressEvent = {
  id: number;
  jobId: number;
  jobNumber: string;
  customerName: string;
  equipment: string;
  status: string;
  assignedTechnicianId: number | null;
  assignedTechnicianName: string | null;
  /** Audit actor. Authoritative when the repair has no assignee. */
  workerUserId: number | null;
  action: string;
  occurredAt: string;
  newValue: string | null;
};

export type OpsFixedRepair = {
  id: number;
  jobNumber: string;
  customerName: string;
  equipment: string;
  status: string;
  assignedTechnicianId: number | null;
  technicianName: string | null;
  completedAt: string;
};

export type ServiceOperationsInput = {
  now: Date;
  audience: ServiceOperationsAudience;
  viewerId: number;
  activeRepairs: OpsActiveRepair[];
  activeVisits: OpsActiveVisit[];
  upcomingVisits: OpsUpcomingVisit[];
  reports: OpsReport[];
  attentionJobs: OpsAttentionJob[];
  progressEvents: OpsProgressEvent[];
  fixedRepairs: OpsFixedRepair[];
  completedRecently: { repairs: number; visits: number };
};

export type ServiceOperationsView = {
  generatedAt: string;
  audience: ServiceOperationsAudience;
  snapshot: {
    workingNow: number;
    visitsToday: number;
    needsAttention: number;
    completedRecently: number;
  };
  happeningNow: {
    repairs: Array<OpsActiveRepair & { stageLabel: string; statusLabel: string }>;
    visits: OpsActiveVisit[];
  };
  upcoming: {
    today: Array<OpsUpcomingVisit & { overdue: boolean; bucket: VisitDayBucket }>;
    tomorrow: Array<OpsUpcomingVisit & { overdue: boolean; bucket: VisitDayBucket }>;
    later: Array<OpsUpcomingVisit & { overdue: boolean; bucket: VisitDayBucket }>;
  };
  latestReports: { items: OpsReport[]; totalBounded: number };
  repairProgress: {
    needsAttention: Array<OpsAttentionJob & { reason: string; reasonCode: string; statusLabel: string }>;
    recentlyProgressed: Array<OpsProgressEvent & { summary: string; category: ServiceActivityCategory }>;
  };
  recentlyFixed: Array<OpsFixedRepair & { whenLabel: string; statusLabel: string }>;
};

export function activeSvrNextAction(): string {
  return "Document the work, then submit the update for PIC review.";
}

export function buildServiceOperations(input: ServiceOperationsInput): ServiceOperationsView {
  const repairs = input.activeRepairs
    .filter((row) =>
      input.audience === "all"
      || row.technicianUserId === input.viewerId
      || row.assignedTechnicianId === input.viewerId,
    )
    .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
    .slice(0, SERVICE_OPS_LIMITS.activeRepairs)
    .map((row) => ({
      ...row,
      stageLabel: roLiveWorkPresentation(row.status, true).label ?? "Repair in progress",
      statusLabel: JOB_STATUS_LABELS[row.status] ?? row.status,
    }));

  const visits = input.activeVisits
    .filter((row) => assignedVisible(input.audience, input.viewerId, row.technicianId, false))
    .slice(0, SERVICE_OPS_LIMITS.activeVisits);

  const grouped = { today: [], tomorrow: [], later: [] } as ServiceOperationsView["upcoming"];
  const upcoming = input.upcomingVisits
    .filter((row) => row.status !== "cancelled")
    .filter((row) => assignedVisible(input.audience, input.viewerId, row.technicianId, false))
    .slice(0, SERVICE_OPS_LIMITS.upcoming);
  for (const row of upcoming) {
    const bucket = upcomingVisitBucket(row.visitDate, input.now);
    if (!bucket) continue;
    grouped[bucket].push({
      ...row,
      bucket,
      overdue: visitOverdue({
        status: row.status,
        visitDate: row.visitDate,
        arrivalWindow: row.arrivalWindow,
        arrivedAt: row.arrivedAt,
        onSite: row.onSite,
        now: input.now,
      }),
    });
  }

  const reports = [...input.reports]
    .filter((row) => assignedVisible(input.audience, input.viewerId, row.technicianId, false))
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
    .slice(0, SERVICE_OPS_LIMITS.reports);

  const needsAttention = input.attentionJobs
    .filter((row) => assignedVisible(input.audience, input.viewerId, row.assignedTechnicianId, true))
    .map((row) => {
      const reason = classifyRoNeedsAttention({ ...row, now: input.now });
      if (!reason) return null;
      return { ...row, reason: reason.label, reasonCode: reason.code, statusLabel: JOB_STATUS_LABELS[row.status] ?? row.status };
    })
    .filter((row): row is NonNullable<typeof row> => row != null)
    .slice(0, SERVICE_OPS_LIMITS.attention);

  const seenJobs = new Set<number>();
  const recentlyProgressed = [...input.progressEvents]
    .filter((row) => isMeaningfulRoProgress(row.action))
    .filter((row) => progressVisible(input.audience, input.viewerId, row))
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
    .filter((row) => {
      if (seenJobs.has(row.jobId)) return false;
      seenJobs.add(row.jobId);
      return true;
    })
    .slice(0, SERVICE_OPS_LIMITS.progress)
    .map((row) => ({
      ...row,
      summary: roProgressSummary(row.action, row.newValue),
      category: categorizeServiceActivity(row.action),
    }));

  const recentlyFixed = [...input.fixedRepairs]
    .filter((row) => assignedVisible(input.audience, input.viewerId, row.assignedTechnicianId, false))
    .filter((row) => row.completedAt.trim() !== "")
    .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime())
    .slice(0, SERVICE_OPS_LIMITS.fixed)
    .map((row) => ({
      ...row,
      whenLabel: recentWhenLabel(row.completedAt, input.now),
      statusLabel: JOB_STATUS_LABELS[row.status] ?? row.status,
    }));

  return {
    generatedAt: input.now.toISOString(),
    audience: input.audience,
    snapshot: {
      workingNow: repairs.length + visits.length,
      visitsToday: grouped.today.length,
      needsAttention: needsAttention.length,
      completedRecently: input.completedRecently.repairs + input.completedRecently.visits,
    },
    happeningNow: { repairs, visits },
    upcoming: grouped,
    latestReports: { items: reports, totalBounded: reports.length },
    repairProgress: { needsAttention, recentlyProgressed },
    recentlyFixed,
  };
}

export function elapsedFromServerStart(startedAt: string | Date, now: Date): number {
  return elapsedSeconds(new Date(startedAt), now);
}
